import { useState, useRef, useCallback, useEffect } from "react";
import { Lock, Scaling } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const OVERLAY_HEIGHT = 340; // Share 페이월 오버레이 높이 (px)
const BLUR_HEIGHT = 100; // Share 블러 영역 높이 (px)

interface PaywallDividerProps {
  contentHtml: string;
  initialFreeHtml?: string;
  onFreeContentChange: (freeHtml: string) => void;
  onTogglePreviewWidth?: (enabled: boolean) => void;
}

export function PaywallDivider({
  contentHtml,
  initialFreeHtml,
  onFreeContentChange,
  onTogglePreviewWidth,
}: PaywallDividerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [dividerPosition, setDividerPosition] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPreviewWidth, setIsPreviewWidth] = useState(false);

  const handleTogglePreviewWidth = useCallback(() => {
    const next = !isPreviewWidth;
    setIsPreviewWidth(next);
    onTogglePreviewWidth?.(next);
  }, [isPreviewWidth, onTogglePreviewWidth]);

  const computeFreeContent = useCallback(
    (yPosition: number) => {
      if (!contentRef.current) return;

      const children = Array.from(contentRef.current.children);
      const freeElements: string[] = [];

      for (const child of children) {
        const rect = child.getBoundingClientRect();
        const containerRect = contentRef.current.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;

        if (relativeTop < yPosition) {
          freeElements.push(child.outerHTML);
        } else {
          break;
        }
      }

      onFreeContentChange(freeElements.join(""));
    },
    [onFreeContentChange]
  );

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !contentRef.current) return;

      const containerRect = contentRef.current.getBoundingClientRect();
      const y = Math.max(0, Math.min(e.clientY - containerRect.top, containerRect.height));
      setDividerPosition(y);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging && dividerPosition !== null) {
      computeFreeContent(dividerPosition);
    }
    setIsDragging(false);
  }, [isDragging, dividerPosition, computeFreeContent]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Initialize divider position from initialFreeHtml
  useEffect(() => {
    if (initialFreeHtml && contentRef.current && dividerPosition === null) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = initialFreeHtml;
      const freeElementCount = tempDiv.children.length;

      const children = Array.from(contentRef.current.children);
      if (freeElementCount > 0 && freeElementCount <= children.length) {
        const lastFreeChild = children[freeElementCount - 1];
        const containerRect = contentRef.current.getBoundingClientRect();
        const childRect = lastFreeChild.getBoundingClientRect();
        setDividerPosition(childRect.bottom - containerRect.top);
      }
    }
  }, [initialFreeHtml, contentHtml, dividerPosition]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!contentRef.current) return;
      const containerRect = contentRef.current.getBoundingClientRect();
      const y = e.clientY - containerRect.top;
      setDividerPosition(y);
      computeFreeContent(y);
    },
    [computeFreeContent]
  );

  return (
    <div className="relative overflow-hidden rounded-lg border">
      <div className="bg-muted/50 flex items-center justify-between border-b px-3 py-2">
        <span className="text-muted-foreground text-xs font-medium">
          클릭하거나 구분선을 드래그하여 무료/프로 구간을 설정하세요
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleTogglePreviewWidth}
                className={`rounded p-1 transition-colors ${
                  isPreviewWidth
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <Scaling className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>미리보기 너비에 맞추기 (738px)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="relative cursor-crosshair select-none" onClick={handleClick}>
        <div
          ref={contentRef}
          className="prose prose-sm dark:prose-invert max-w-none p-4"
          style={isPreviewWidth ? { maxWidth: "738px", margin: "0 auto" } : undefined}
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />

        {/* 오버레이 프리뷰 존: 구분선 위 340px 그라데이션 (블러 없이 힌트만) */}
        {dividerPosition !== null && (
          <div
            className="pointer-events-none absolute left-0 right-0 z-[5]"
            style={{
              top: `${Math.max(0, dividerPosition - OVERLAY_HEIGHT)}px`,
              height: `${Math.min(OVERLAY_HEIGHT, dividerPosition)}px`,
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 70%, rgba(255,255,255,1) 100%)",
            }}
          />
        )}

        {/* 안내선: "여기까지 보입니다" (오버레이 존 상단) */}
        {/*{dividerPosition !== null && dividerPosition > OVERLAY_HEIGHT && (
          <div
            className="absolute left-0 right-0 z-10 flex items-center"
            style={{ top: `${dividerPosition - OVERLAY_HEIGHT}px` }}
          >
            <div className="flex-1 border-t border-dashed border-gray-300" />
            <span className="text-muted-foreground bg-background/80 rounded px-2 py-0.5 text-[10px]">
              여기까지 보입니다
            </span>
            <div className="flex-1 border-t border-dashed border-gray-300" />
          </div>
        )}*/}

        {/* 메인 페이월 구분선 */}
        {dividerPosition !== null && (
          <div className="absolute left-0 right-0 z-10 flex items-center" style={{ top: `${dividerPosition}px` }}>
            <div className="flex-1 border-t-2 border-dashed border-amber-500" />
            <div
              className="flex cursor-grab select-none items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-medium text-white active:cursor-grabbing"
              onMouseDown={handleMouseDown}
            >
              <Lock className="h-3 w-3" />
              페이월
            </div>
            <div className="flex-1 border-t-2 border-dashed border-amber-500" />
          </div>
        )}

        {/* 유료 영역 (구분선 아래) */}
        {dividerPosition !== null && (
          <div
            className="bg-muted/30 pointer-events-none absolute bottom-0 left-0 right-0"
            style={{ top: `${dividerPosition}px` }}
          />
        )}
      </div>
    </div>
  );
}
